import { useState, useEffect } from 'react';
import { Link } from 'wouter';
import { ArrowLeft, ChevronLeft, ChevronRight, Home } from 'lucide-react';
import { LectureDownloader } from '@/components/LectureDownloader';

export function BrainHealthNeurofeedback() {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' && currentSlide > 0) {
        setCurrentSlide(prev => prev - 1);
      } else if (event.key === 'ArrowRight' && currentSlide < slides.length - 1) {
        setCurrentSlide(prev => prev + 1);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentSlide]);

  const slides = [
    {
      title: "Optimizing Brain Longevity: Integrating Neurofeedback, Quantitative EEG, and Electroceuticals into Clinical Practice",
      content: (
        <div className="flex flex-col items-center justify-center h-full text-center">
          <h1 className="text-4xl font-light text-gray-900 mb-6">
            Optimizing Brain Longevity
          </h1>
          <h2 className="text-2xl font-light text-blue-600 mb-8">
            Integrating Neurofeedback, Quantitative EEG, and Electroceuticals into Clinical Practice
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            A Clinician's Introduction to Advanced Neurotechnologies for Enhanced Healthspan
          </p>
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <p className="text-sm text-blue-800">
              Next Generation Medicine • Longevity Science
            </p>
          </div>
        </div>
      )
    },
    {
      title: "The Imperative of Brain Longevity",
      content: (
        <div className="grid grid-cols-2 gap-8 h-full">
          <div className="bg-red-50 border border-red-200 p-6 rounded-lg">
            <h3 className="text-xl font-medium text-red-800 mb-4 flex items-center">
              <svg className="w-6 h-6 mr-3" viewBox="0 0 100 80" xmlns="http://www.w3.org/2000/svg">
                <ellipse cx="50" cy="40" rx="35" ry="25" fill="#ccc" stroke="#999" strokeWidth="2"/>
                <circle cx="35" cy="30" r="3" fill="#666"/>
                <circle cx="65" cy="30" r="3" fill="#666"/>
                <path d="M30 50 Q50 60 70 50" stroke="#666" strokeWidth="2" fill="none"/>
              </svg>
              The Challenge
            </h3>
            <ul className="space-y-3 text-sm text-gray-700">
              <li><strong>Normal aging:</strong> Subtle declines (processing speed, recall)¹</li>
              <li><strong>Pathological neurodegeneration</strong> (e.g., Alzheimer's) significantly impacts healthspan</li>
              <li><strong>Cognitive vitality</strong> is a cornerstone of longevity medicine³</li>
            </ul>
          </div>
          
          <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
            <h3 className="text-xl font-medium text-green-800 mb-4 flex items-center">
              <svg className="w-6 h-6 mr-3" viewBox="0 0 100 80" xmlns="http://www.w3.org/2000/svg">
                <ellipse cx="50" cy="40" rx="35" ry="25" fill="#90EE90" stroke="#32CD32" strokeWidth="2"/>
                <circle cx="35" cy="30" r="3" fill="#006400"/>
                <circle cx="65" cy="30" r="3" fill="#006400"/>
                <path d="M30 50 Q50 40 70 50" stroke="#006400" strokeWidth="2" fill="none"/>
                <circle cx="25" cy="25" r="2" fill="#FFD700"/>
                <circle cx="75" cy="25" r="2" fill="#FFD700"/>
                <circle cx="50" cy="20" r="2" fill="#FFD700"/>
              </svg>
              The Opportunity: Neuro-Longevity
            </h3>
            <ul className="space-y-3 text-sm text-gray-700">
              <li><strong>Shift</strong> from reactive (treating dementia) to proactive optimization</li>
              <li><strong>Definition:</strong> Proactive strategies to enhance brain resilience, neuroplasticity, and cognitive reserve³</li>
              <li><strong>Goal:</strong> Not just absence of disease, but peak cognitive capabilities⁵</li>
              <li><strong>Focus:</strong> Synaptic health and leveraging the brain's adaptive capacity²,³</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      title: "Overview of Advanced Modalities",
      content: (
        <div className="grid grid-cols-3 gap-6 h-full">
          <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg text-center">
            <svg className="w-16 h-16 mx-auto mb-4" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <circle cx="50" cy="50" r="40" fill="none" stroke="#3B82F6" strokeWidth="2"/>
              <rect x="10" y="45" width="80" height="10" fill="#3B82F6" opacity="0.3"/>
              <rect x="15" y="30" width="70" height="8" fill="#10B981" opacity="0.5"/>
              <rect x="20" y="60" width="60" height="12" fill="#F59E0B" opacity="0.4"/>
              <text x="50" y="85" textAnchor="middle" fontSize="8" fill="#374151">Brain Map</text>
            </svg>
            <h3 className="text-lg font-medium text-blue-800 mb-3">Quantitative EEG (qEEG)</h3>
            <ul className="text-sm text-gray-700 space-y-2">
              <li>Digital analysis of brain's electrical activity⁸</li>
              <li>Provides "brain maps" revealing activity patterns¹⁰</li>
              <li>Foundation for personalizing interventions</li>
            </ul>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-6 rounded-lg text-center">
            <svg className="w-16 h-16 mx-auto mb-4" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <circle cx="50" cy="50" r="30" fill="none" stroke="#8B5CF6" strokeWidth="2"/>
              <path d="M30 50 Q50 30 70 50 Q50 70 30 50" fill="none" stroke="#10B981" strokeWidth="2"/>
              <circle cx="50" cy="50" r="3" fill="#EF4444"/>
              <path d="M50 50 L75 25" stroke="#F59E0B" strokeWidth="2" markerEnd="url(#arrow)"/>
              <defs>
                <marker id="arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                  <polygon points="0 0, 6 3, 0 6" fill="#F59E0B"/>
                </marker>
              </defs>
              <text x="80" y="20" fontSize="8" fill="#374151">Feedback</text>
            </svg>
            <h3 className="text-lg font-medium text-purple-800 mb-3">Neurofeedback (NFB)</h3>
            <ul className="text-sm text-gray-700 space-y-2">
              <li>Brain training via real-time feedback¹¹</li>
              <li>Learn self-regulation of brainwave activity</li>
              <li>Enhances neuroplasticity and cognition¹²</li>
            </ul>
          </div>

          <div className="bg-orange-50 border border-orange-200 p-6 rounded-lg text-center">
            <svg className="w-16 h-16 mx-auto mb-4" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <circle cx="50" cy="50" r="30" fill="none" stroke="#F97316" strokeWidth="2"/>
              <path d="M35 50 L45 40 L55 60 L65 50" stroke="#EF4444" strokeWidth="3" fill="none"/>
              <circle cx="30" cy="30" r="4" fill="#10B981"/>
              <circle cx="70" cy="30" r="4" fill="#10B981"/>
              <line x1="30" y1="30" x2="45" y2="45" stroke="#10B981" strokeWidth="2"/>
              <line x1="70" y1="30" x2="55" y2="45" stroke="#10B981" strokeWidth="2"/>
              <text x="50" y="85" textAnchor="middle" fontSize="8" fill="#374151">Stimulation</text>
            </svg>
            <h3 className="text-lg font-medium text-orange-800 mb-3">Electroceuticals</h3>
            <ul className="text-sm text-gray-700 space-y-2">
              <li>Electrical/magnetic stimulation to modulate brain activity¹³</li>
              <li>Range: Non-invasive (tDCS, TMS) to invasive (VNS, DBS)¹⁴</li>
              <li>Emerging technologies and applications</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      title: "qEEG: From Brainwaves to Brain Maps",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">What is qEEG?</h3>
            <p className="text-sm text-gray-700">
              Non-invasive measurement and computational analysis of cortical electrical activity⁸
            </p>
          </div>

          <div className="bg-white border border-gray-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-gray-800 mb-4">qEEG Processing Flow</h3>
            <svg width="100%" height="150" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 150">
              <defs>
                <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" style={{stopColor:"rgb(255,255,0)", stopOpacity:1}} />
                  <stop offset="100%" style={{stopColor:"rgb(255,0,0)", stopOpacity:1}} />
                </linearGradient>
              </defs>
              {/* Raw EEG Wave */}
              <path d="M10 75 Q 20 50, 30 75 T 50 75 Q 60 100, 70 75 T 90 75" stroke="blue" strokeWidth="2" fill="none"/>
              <text x="10" y="100" fontFamily="Arial" fontSize="10">Raw EEG</text>
              <text x="100" y="75" fontFamily="Arial" fontSize="20">→</text>
              {/* Frequency Bands */}
              <rect x="130" y="50" width="30" height="50" fill="lightblue"/>
              <text x="132" y="115" fontFamily="Arial" fontSize="10">Delta</text>
              <rect x="165" y="60" width="30" height="40" fill="lightgreen"/>
              <text x="167" y="115" fontFamily="Arial" fontSize="10">Theta</text>
              <rect x="200" y="40" width="30" height="60" fill="lightyellow"/>
              <text x="202" y="115" fontFamily="Arial" fontSize="10">Alpha</text>
              <rect x="235" y="65" width="30" height="35" fill="lightcoral"/>
              <text x="237" y="115" fontFamily="Arial" fontSize="10">Beta</text>
              <text x="130" y="30" fontFamily="Arial" fontSize="10">Power Spectrum</text>
              <text x="275" y="75" fontFamily="Arial" fontSize="20">→</text>
              {/* Brain Map */}
              <circle cx="335" cy="75" r="30" fill="url(#grad1)" stroke="black" strokeWidth="1"/>
              <text x="310" y="120" fontFamily="Arial" fontSize="10">Brain Map</text>
            </svg>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
              <h4 className="font-medium text-gray-800 mb-2">Key qEEG Metrics</h4>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• <strong>Power Spectrum:</strong> Absolute & relative power in frequency bands¹⁰</li>
                <li>• <strong>Coherence:</strong> Synchrony between regions¹⁰</li>
                <li>• <strong>Phase Lag:</strong> Timing of information flow</li>
                <li>• <strong>Amplitude Asymmetry:</strong> Hemispheric balance</li>
              </ul>
            </div>
            <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
              <h4 className="font-medium text-gray-800 mb-2">Advanced Analysis</h4>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• <strong>Brain Network Analysis:</strong> Topology, efficiency</li>
                <li>• <strong>Microstate Analysis:</strong> Global activity patterns¹⁰</li>
                <li>• <strong>Complexity/Entropy:</strong> Signal predictability²</li>
                <li>• <strong>Normative Databases:</strong> Age-matched comparisons²⁰</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "qEEG Biomarkers: Differentiating Brain Aging",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-yellow-800 mb-3">Normal Aging</h3>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• Slowing of Peak Alpha Frequency (PAF)²</li>
                <li>• Potential increase in slow-wave (delta/theta)²²</li>
                <li>• Altered power distribution</li>
                <li>• Aperiodic slope decrease (↑excitability)²²</li>
              </ul>
            </div>

            <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-red-800 mb-3">Cognitive Decline/MCI/AD</h3>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• More pronounced ↑theta, ↓alpha (posterior)¹⁰,¹⁹</li>
                <li>• ↓complexity, ↓alpha connectivity</li>
                <li>• Disrupted network topology</li>
                <li>• Altered microstate dynamics</li>
              </ul>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-4">EEG-Age ("Brain Age")</h3>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="text-center">
                <div className="bg-green-100 border border-green-300 p-3 rounded-lg mb-2">
                  <strong className="text-green-800">EEG-Age &lt; Chronological Age</strong>
                </div>
                <p className="text-gray-700">Preserved integrity, slower brain aging (desirable)²¹</p>
              </div>
              <div className="text-center">
                <div className="bg-yellow-100 border border-yellow-300 p-3 rounded-lg mb-2">
                  <strong className="text-yellow-800">EEG-Age = Chronological Age</strong>
                </div>
                <p className="text-gray-700">Typical aging trajectory</p>
              </div>
              <div className="text-center">
                <div className="bg-red-100 border border-red-300 p-3 rounded-lg mb-2">
                  <strong className="text-red-800">EEG-Age &gt; Chronological Age</strong>
                </div>
                <p className="text-gray-700">May indicate accelerated brain aging/pathology²¹</p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Signatures of Cognitive Resilience/High Function</h3>
            <ul className="text-sm text-gray-700 space-y-2">
              <li>• Higher relative parietal alpha power, higher parietal alpha/theta ratio⁷</li>
              <li>• Lower resting theta⁷</li>
              <li>• Efficient, dynamic coherence patterns (not just "more" coherence)⁷,¹⁶</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      title: "qEEG Patterns: Clinical Interpretation Guide",
      content: (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 p-2 text-left">qEEG Metric</th>
                <th className="border border-gray-300 p-2 text-left">Optimal Aging/Resilience</th>
                <th className="border border-gray-300 p-2 text-left">Early Decline/MCI</th>
                <th className="border border-gray-300 p-2 text-left">Dementia/AD</th>
                <th className="border border-gray-300 p-2 text-left">Clinical Implication</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-white">
                <td className="border border-gray-300 p-2"><strong>Peak Alpha Freq (PAF)</strong></td>
                <td className="border border-gray-300 p-2 bg-green-50">Stable/High normal²</td>
                <td className="border border-gray-300 p-2 bg-yellow-50">Pronounced decrease</td>
                <td className="border border-gray-300 p-2 bg-red-50">Significantly slowed</td>
                <td className="border border-gray-300 p-2">Cortical processing speed/integrity</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="border border-gray-300 p-2"><strong>Theta Power (4-8 Hz)</strong></td>
                <td className="border border-gray-300 p-2 bg-green-50">Low resting theta⁷</td>
                <td className="border border-gray-300 p-2 bg-yellow-50">Increased theta¹⁰</td>
                <td className="border border-gray-300 p-2 bg-red-50">Markedly increased theta</td>
                <td className="border border-gray-300 p-2">High theta: inattention, cortical hypofunction</td>
              </tr>
              <tr className="bg-white">
                <td className="border border-gray-300 p-2"><strong>Alpha Power (8-13 Hz)</strong></td>
                <td className="border border-gray-300 p-2 bg-green-50">Robust/Compensatory ↑²²</td>
                <td className="border border-gray-300 p-2 bg-yellow-50">Decreased (posterior)¹⁰</td>
                <td className="border border-gray-300 p-2 bg-red-50">Significantly decreased</td>
                <td className="border border-gray-300 p-2">Reduced alpha: cortical dysfunction</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="border border-gray-300 p-2"><strong>Theta/Beta Ratio (TBR)</strong></td>
                <td className="border border-gray-300 p-2 bg-green-50">Low/Balanced</td>
                <td className="border border-gray-300 p-2 bg-yellow-50">Often increased</td>
                <td className="border border-gray-300 p-2 bg-red-50">Typically increased</td>
                <td className="border border-gray-300 p-2">High TBR: inattention, cognitive inefficiency²⁶</td>
              </tr>
              <tr className="bg-white">
                <td className="border border-gray-300 p-2"><strong>Alpha/Theta Ratio</strong></td>
                <td className="border border-gray-300 p-2 bg-green-50">High parietal⁷</td>
                <td className="border border-gray-300 p-2 bg-yellow-50">Often decreased</td>
                <td className="border border-gray-300 p-2 bg-red-50">Significantly decreased</td>
                <td className="border border-gray-300 p-2">Linked to cognitive training outcomes</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="border border-gray-300 p-2"><strong>Coherence (Connectivity)</strong></td>
                <td className="border border-gray-300 p-2 bg-green-50">Efficient, dynamic⁷</td>
                <td className="border border-gray-300 p-2 bg-yellow-50">↓ Alpha coherence¹⁸</td>
                <td className="border border-gray-300 p-2 bg-red-50">Predominantly ↓ alpha coherence</td>
                <td className="border border-gray-300 p-2">Inter-regional communication; "disconnection"</td>
              </tr>
              <tr className="bg-white">
                <td className="border border-gray-300 p-2"><strong>EEG-Age vs. Chrono. Age</strong></td>
                <td className="border border-gray-300 p-2 bg-green-50">EEG-Age &lt; Chrono.²¹</td>
                <td className="border border-gray-300 p-2 bg-yellow-50">EEG-Age &gt; Chrono.</td>
                <td className="border border-gray-300 p-2 bg-red-50">EEG-Age &gt;&gt; Chrono.</td>
                <td className="border border-gray-300 p-2">Deviation from typical brain aging</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="border border-gray-300 p-2"><strong>Complexity/Entropy</strong></td>
                <td className="border border-gray-300 p-2 bg-green-50">High complexity²</td>
                <td className="border border-gray-300 p-2 bg-yellow-50">Reduced complexity</td>
                <td className="border border-gray-300 p-2 bg-red-50">Markedly reduced complexity</td>
                <td className="border border-gray-300 p-2">Adaptable/efficient processing; reduced in AD/MCI</td>
              </tr>
            </tbody>
          </table>
        </div>
      )
    },
    {
      title: "qEEG in Longevity Practice: Clinical Utility",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
              <div className="flex items-center mb-4">
                <svg className="w-8 h-8 mr-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <h3 className="text-lg font-medium text-blue-800">1. Baseline Assessment & Personalized Planning</h3>
              </div>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• Establish neurophysiological baseline, even in asymptomatic individuals⁷</li>
                <li>• Identify subtle dysregulations <em>before</em> clinical decline</li>
                <li>• <strong>qEEG-informed Neurofeedback:</strong> Guides NFB protocol selection²⁰,²⁶</li>
                <li>• May inform selection of electroceuticals or adjunctive therapies</li>
              </ul>
            </div>

            <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
              <div className="flex items-center mb-4">
                <svg className="w-8 h-8 mr-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <h3 className="text-lg font-medium text-green-800">2. Monitoring Treatment Efficacy</h3>
              </div>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• Objectively track changes in brain activity post-intervention⁸</li>
                <li>• Confirm neurophysiological improvements²⁰</li>
                <li>• Guide protocol adjustments based on response</li>
                <li>• Proactive management of cognitive resilience over lifespan</li>
              </ul>
            </div>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Example qEEG-Guided Protocol Selection</h3>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="bg-white p-3 rounded border">
                <strong className="text-purple-700">Excess Theta</strong>
                <p className="text-gray-600 mt-1">→ Theta-down/Beta-up NFB</p>
              </div>
              <div className="bg-white p-3 rounded border">
                <strong className="text-purple-700">Low Alpha</strong>
                <p className="text-gray-600 mt-1">→ Alpha up-training</p>
              </div>
              <div className="bg-white p-3 rounded border">
                <strong className="text-purple-700">Poor Connectivity</strong>
                <p className="text-gray-600 mt-1">→ Coherence training</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Neurofeedback: Training the Brain for Longevity",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-4">Core Principle: Operant Conditioning¹¹,²⁸</h3>
            <ul className="text-sm text-gray-700 space-y-2">
              <li>• Real-time EEG measurement + sensory feedback (visual/auditory)</li>
              <li>• Desired brain activity patterns are rewarded → reinforced</li>
              <li>• Individual learns volitional control over specific brain functions</li>
            </ul>
          </div>

          <div className="bg-white border border-gray-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Neurofeedback Process Flow</h3>
            <svg width="100%" height="200" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 450 200">
              {/* Head with EEG sensors */}
              <circle cx="70" cy="100" r="40" stroke="black" fill="#eee"/>
              <circle cx="60" cy="80" r="3" fill="red"/>
              <circle cx="80" cy="80" r="3" fill="red"/>
              <line x1="70" y1="60" x2="150" y2="60" stroke="gray" strokeWidth="2"/>
              <text x="50" y="150" fontFamily="Arial" fontSize="12">Brain Activity</text>
              {/* Computer */}
              <rect x="150" y="50" width="80" height="60" stroke="black" fill="lightgray"/>
              <line x1="150" y1="110" x2="150" y2="140" stroke="gray" strokeWidth="2"/>
              <text x="160" y="150" fontFamily="Arial" fontSize="12">Processing</text>
              {/* Feedback Display */}
              <rect x="270" y="50" width="100" height="70" stroke="black" fill="lightblue"/>
              <polygon points="300,60 300,110 340,85" fill="green"/>
              <line x1="270" y1="85" x2="70" y2="85" stroke="gray" strokeDasharray="5,5" strokeWidth="2"/>
              <text x="300" y="150" fontFamily="Arial" fontSize="12">Real-time Feedback</text>
              <text x="180" y="30" fontFamily="Arial" fontSize="14" fill="blue">Operant Conditioning Loop</text>
            </svg>
          </div>

          <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-4">Underlying Mechanism: Neuroplasticity³,¹²</h3>
            <ul className="text-sm text-gray-700 space-y-2">
              <li>• Brain's ability to reorganize structure, function, connections</li>
              <li>• NFB guides brain activity, strengthening underlying neural pathways</li>
              <li>• Likely involves Long-Term Potentiation (LTP) / Long-Term Depression (LTD) like effects³⁰,³¹</li>
              <li>• Potential modulation of neurotrophic factors (e.g., BDNF)³⁶,⁴¹</li>
              <li>• Aims to counteract age-related detrimental synaptic changes⁴</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      title: "Common Neurofeedback Protocols for Longevity",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">qEEG-Guided Protocol Selection</h3>
            <p className="text-sm text-gray-700">
              Protocol choice personalized by individual brain map²⁰
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 p-3 text-left">Protocol Name</th>
                  <th className="border border-gray-300 p-3 text-left">Target Brainwave(s) & Direction</th>
                  <th className="border border-gray-300 p-3 text-left">Primary Longevity Goal(s)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-white">
                  <td className="border border-gray-300 p-3"><strong>Alpha Up-training</strong></td>
                  <td className="border border-gray-300 p-3">Increase Alpha (8-13 Hz)</td>
                  <td className="border border-gray-300 p-3">Stress Reduction, Mental Clarity, Cognitive Performance¹¹</td>
                </tr>
                <tr className="bg-blue-50">
                  <td className="border border-gray-300 p-3"><strong>SMR Up-training</strong></td>
                  <td className="border border-gray-300 p-3">Increase SMR (12-15 Hz)</td>
                  <td className="border border-gray-300 p-3">Attention, Working Memory, Sleep Quality¹¹,³⁰</td>
                </tr>
                <tr className="bg-white">
                  <td className="border border-gray-300 p-3"><strong>Theta Up-training</strong></td>
                  <td className="border border-gray-300 p-3">Increase Theta (4-8 Hz)</td>
                  <td className="border border-gray-300 p-3">Creativity, Insight, Memory Consolidation¹¹,⁴³</td>
                </tr>
                <tr className="bg-yellow-50">
                  <td className="border border-gray-300 p-3"><strong>TBR Training</strong></td>
                  <td className="border border-gray-300 p-3">↓Theta / ↑Beta (or ↓ratio)</td>
                  <td className="border border-gray-300 p-3">Improved Focus, Reduced Brain Fog²⁶</td>
                </tr>
                <tr className="bg-white">
                  <td className="border border-gray-300 p-3"><strong>LORETA NFB</strong></td>
                  <td className="border border-gray-300 p-3">Modulate deep regions/networks</td>
                  <td className="border border-gray-300 p-3">Address Complex Dysregulations¹¹</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <h4 className="font-medium text-green-800 mb-2">Key for Aging</h4>
              <p className="text-sm text-gray-700">
                <strong>SMR Up-training</strong> is particularly important for cognitive enhancement in aging, targeting attention, working memory, and sleep quality.
              </p>
            </div>
            <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
              <h4 className="font-medium text-purple-800 mb-2">Combination Approaches</h4>
              <p className="text-sm text-gray-700">
                Often combine protocols (e.g., Theta + SMR) for comprehensive cognitive enhancement and memory consolidation.
              </p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Neurofeedback: Clinical Evidence in Aging",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-4">Improving Cognitive Domains</h3>
            
            <div className="space-y-4">
              <div className="bg-white border border-gray-200 p-4 rounded-lg">
                <h4 className="font-medium text-gray-800 mb-2">Memory</h4>
                <p className="text-sm text-gray-700 mb-2">
                  NFB (esp. Theta & SMR training) improved memory in healthy older adults & MCI⁴³
                </p>
                
                <div className="bg-yellow-50 border border-yellow-200 p-3 rounded mt-3">
                  <h5 className="font-medium text-yellow-800 mb-2">Study Example: Jiang et al. (2021) Meta-analysis⁴⁴</h5>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li><strong>Finding:</strong> Moderate effect sizes for NFB improving working memory (WM) and episodic memory (EM) in elderly/MCI</li>
                    <li><strong>Critique:</strong> Heterogeneity in protocols, need for larger, standardized trials</li>
                  </ul>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white border border-gray-200 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-800 mb-2">Attention</h4>
                  <p className="text-sm text-gray-700">
                    SMR protocol known for attention benefits³⁰. Some reviews mixed on attention in elderly⁴³ - protocol dependent.
                  </p>
                </div>
                <div className="bg-white border border-gray-200 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-800 mb-2">Executive Function</h4>
                  <p className="text-sm text-gray-700">
                    Working memory improvements with SMR NFB³⁰
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-green-800 mb-3">Age-Related Cognitive Decline & MCI</h3>
              <p className="text-sm text-gray-700">
                Promising for MCI; NFB targeting high theta showed EEG & behavioral improvements²⁸,⁴⁵
              </p>
            </div>

            <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-purple-800 mb-3">Stress Reduction & Cognitive Resilience</h3>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• Alpha up-training for stress reduction¹¹</li>
                <li>• May lower inflammatory markers (e.g., CRP)⁴⁶</li>
                <li>• Influence HRV⁵⁰</li>
              </ul>
            </div>
          </div>

          <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-orange-800 mb-3">Considerations</h3>
            <p className="text-sm text-gray-700">
              Placebo effects, individual variability. qEEG-guidance aims to improve specificity¹¹,²⁰,²⁹
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Neurofeedback: Practical Considerations",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-3 gap-6">
            <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg text-center">
              <svg className="w-12 h-12 mx-auto mb-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h3 className="text-lg font-medium text-blue-800 mb-3">Session Structure</h3>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• 20-30 min active training per session⁵⁰</li>
                <li>• 10-40+ sessions typical</li>
                <li>• 1-3 times/week¹¹</li>
                <li>• &gt;300 min total for memory benefits⁴⁴</li>
              </ul>
            </div>

            <div className="bg-green-50 border border-green-200 p-6 rounded-lg text-center">
              <svg className="w-12 h-12 mx-auto mb-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a1 1 0 01-1-1V9a1 1 0 011-1h1a2 2 0 100-4H4a1 1 0 01-1-1V4a1 1 0 011-1h3a1 1 0 011 1v1z" />
              </svg>
              <h3 className="text-lg font-medium text-green-800 mb-3">Integration</h3>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• Lifestyle coaching²⁶</li>
                <li>• Photobiomodulation (PBM)³³</li>
                <li>• Nutraceuticals/Peptides</li>
                <li>• Psychotherapy, cognitive training⁵⁴</li>
              </ul>
            </div>

            <div className="bg-purple-50 border border-purple-200 p-6 rounded-lg text-center">
              <svg className="w-12 h-12 mx-auto mb-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-lg font-medium text-purple-800 mb-3">Biomarkers</h3>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• qEEG Changes⁸</li>
                <li>• Cognitive Test Improvements³⁰</li>
                <li>• Functional Connectivity⁵⁵</li>
                <li>• Subjective Reports</li>
              </ul>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-800 mb-4">Biomarkers of Efficacy</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium text-gray-800 mb-2">Objective Measures</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• <strong>qEEG Changes:</strong> Normalization/optimization of targeted brainwaves⁸</li>
                  <li>• <strong>Cognitive Tests:</strong> Standardized neuropsych testing³⁰</li>
                  <li>• <strong>Functional Connectivity:</strong> Advanced qEEG/fMRI⁵⁵</li>
                  <li>• <strong>Peripheral Biomarkers:</strong> BDNF, inflammatory markers⁴¹</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-gray-800 mb-2">Subjective Measures</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Mood improvements</li>
                  <li>• Sleep quality</li>
                  <li>• Stress levels</li>
                  <li>• Daily function</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Electroceuticals: Targeted Brain Modulation",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-4">Definition & Goal</h3>
            <p className="text-sm text-gray-700 mb-3">
              <strong>Definition:</strong> Therapeutic interventions using electrical/magnetic stimulation to modulate neural activity⁵⁶
            </p>
            <p className="text-sm text-gray-700">
              <strong>Goal:</strong> Achieve therapeutic effects with greater organ specificity and potentially fewer systemic side effects than drugs⁵⁷
            </p>
          </div>

          <div className="bg-white border border-gray-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Spectrum of Electroceuticals</h3>
            <svg width="100%" height="150" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 150">
              <text x="10" y="20" fontFamily="Arial" fontSize="14">Spectrum of Electroceuticals</text>
              {/* NIBS */}
              <rect x="20" y="40" width="100" height="30" fill="lightgreen"/>
              <text x="25" y="60" fontFamily="Arial" fontSize="10">NIBS (tDCS, TMS)</text>
              <text x="50" y="90" fontFamily="Arial" fontSize="10">Less Invasive</text>
              {/* VNS */}
              <rect x="150" y="40" width="100" height="30" fill="lightyellow"/>
              <text x="155" y="60" fontFamily="Arial" fontSize="10">VNS (tVNS, iVNS)</text>
              {/* DBS/Implants */}
              <rect x="280" y="40" width="100" height="30" fill="lightcoral"/>
              <text x="285" y="60" fontFamily="Arial" fontSize="10">DBS, Implants</text>
              <text x="300" y="90" fontFamily="Arial" fontSize="10">More Invasive</text>
              <line x1="20" y1="110" x2="380" y2="110" stroke="black" markerEnd="url(#arrowhead)"/>
              <text x="150" y="130" fontFamily="Arial" fontSize="10">Increasing Precision/Potency (Potentially)</text>
              <defs>
                <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7"/>
                </marker>
              </defs>
            </svg>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <h4 className="font-medium text-green-800 mb-2">Non-Invasive Brain Stimulation (NIBS)</h4>
              <p className="text-sm text-gray-700">Applied externally (tDCS, TMS, tACS, CES)¹³,¹⁴</p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
              <h4 className="font-medium text-yellow-800 mb-2">Minimally/Invasive</h4>
              <p className="text-sm text-gray-700">Vagus Nerve Stimulation (VNS - invasive & transcutaneous), Deep Brain Stimulation (DBS)</p>
            </div>
            <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
              <h4 className="font-medium text-red-800 mb-2">Emerging</h4>
              <p className="text-sm text-gray-700">Advanced brain implants, neuromorphic chips¹⁵</p>
            </div>
          </div>

          <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-orange-800 mb-3">Key Consideration</h3>
            <p className="text-sm text-gray-700">
              Trade-off between precision/efficacy and invasiveness. NIBS often first-line for longevity in healthier individuals.
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Key Electroceutical Modalities for Brain Longevity",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-4">Non-Invasive Brain Stimulation (NIBS)</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded border">
                <h4 className="font-medium text-blue-700 mb-2">tDCS</h4>
                <p className="text-sm text-gray-700">Weak direct current; anodal enhances, cathodal reduces cortical excitability. Modulates neuroplasticity¹²</p>
              </div>
              <div className="bg-white p-4 rounded border">
                <h4 className="font-medium text-blue-700 mb-2">TMS</h4>
                <p className="text-sm text-gray-700">Magnetic pulses induce current, depolarizing neurons. rTMS for lasting changes¹³</p>
              </div>
              <div className="bg-white p-4 rounded border">
                <h4 className="font-medium text-blue-700 mb-2">tACS</h4>
                <p className="text-sm text-gray-700">Oscillating current to entrain brain rhythms¹³</p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Vagus Nerve Stimulation (VNS)</h3>
            <p className="text-sm text-gray-700">
              Stimulates vagus nerve (invasive or transcutaneous). Modulates autonomic nervous system, anti-inflammatory effects¹³
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 p-3 text-left">Modality</th>
                  <th className="border border-gray-300 p-3 text-left">Mechanism (Brief)</th>
                  <th className="border border-gray-300 p-3 text-left">Potential Longevity Applications</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-white">
                  <td className="border border-gray-300 p-3"><strong>tDCS</strong></td>
                  <td className="border border-gray-300 p-3">Modulates cortical excitability (DC)¹²</td>
                  <td className="border border-gray-300 p-3">Cognitive Enhancement, Mood Support</td>
                </tr>
                <tr className="bg-blue-50">
                  <td className="border border-gray-300 p-3"><strong>rTMS</strong></td>
                  <td className="border border-gray-300 p-3">Alters cortical excitability (magnetic)¹³</td>
                  <td className="border border-gray-300 p-3">Mood Regulation, Cognitive Enhancement</td>
                </tr>
                <tr className="bg-white">
                  <td className="border border-gray-300 p-3"><strong>tACS</strong></td>
                  <td className="border border-gray-300 p-3">Entrains brain oscillations (AC)¹³</td>
                  <td className="border border-gray-300 p-3">Rhythm Synchronization, Cognitive State Modulation</td>
                </tr>
                <tr className="bg-green-50">
                  <td className="border border-gray-300 p-3"><strong>tVNS/VNS</strong></td>
                  <td className="border border-gray-300 p-3">Vagus nerve modulation¹³</td>
                  <td className="border border-gray-300 p-3">Anti-inflammatory, Autonomic Reg., Mood, Cognition</td>
                </tr>
                <tr className="bg-white">
                  <td className="border border-gray-300 p-3"><strong>DBS</strong></td>
                  <td className="border border-gray-300 p-3">Deep brain structure stimulation¹³</td>
                  <td className="border border-gray-300 p-3">Neuroprotection (AD - investigational)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )
    },
    {
      title: "Electroceuticals: Mechanisms & Clinical Examples",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-4">Mechanisms of Action¹²,¹³,⁵⁶,⁶⁰</h3>
            <ul className="text-sm text-gray-700 space-y-2">
              <li>• Modulating neuronal excitability & network activity</li>
              <li>• Promoting neuroprotection, reducing neuroinflammation (e.g., VNS & cholinergic anti-inflammatory pathway)</li>
              <li>• Enhancing cellular clearance (e.g., autophagy - REMFS preclinical¹³)</li>
              <li>• Influencing neurotrophic factors (e.g., BDNF with tDCS, rTMS¹³)</li>
            </ul>
          </div>

          <div className="space-y-6">
            <div className="bg-white border border-gray-200 p-6 rounded-lg">
              <h3 className="text-lg font-medium text-gray-800 mb-4">tDCS for Cognitive Enhancement</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Parameters</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• <strong>Target:</strong> DLPFC</li>
                    <li>• <strong>Settings:</strong> Anodal, 1-2mA</li>
                    <li>• <strong>Duration:</strong> 15-30 min</li>
                    <li>• <strong>Protocol:</strong> Multiple sessions⁵⁸</li>
                  </ul>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 p-3 rounded">
                  <h4 className="font-medium text-yellow-800 mb-2">Study Finding</h4>
                  <p className="text-sm text-gray-700">
                    <strong>Meta-analysis:</strong> tDCS showed beneficial effects on global cognitive measures and memory in AD patients¹³
                  </p>
                  <p className="text-xs text-gray-600 mt-2">
                    <strong>Critique:</strong> Research in healthy aging for pure enhancement ongoing; needs more large-scale trials
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 p-6 rounded-lg">
              <h3 className="text-lg font-medium text-gray-800 mb-4">TMS for Mood & Cognitive Support</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Parameters</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• <strong>Target:</strong> Left DLPFC (depression)</li>
                    <li>• <strong>Settings:</strong> HF-rTMS (e.g., 10Hz)</li>
                    <li>• <strong>Protocol:</strong> Daily for 4-6 weeks¹³</li>
                  </ul>
                </div>
                <div className="bg-green-50 border border-green-200 p-3 rounded">
                  <h4 className="font-medium text-green-800 mb-2">Evidence</h4>
                  <p className="text-sm text-gray-700">
                    HF-rTMS improved attention, memory, MMSE in mild AD¹³. Role in healthy aging cognition under investigation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Core Longevity Pathways & Brain Aging",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Why They Matter</h3>
            <p className="text-sm text-gray-700">
              Fundamental biological pathways governing cellular/systemic aging also critically impact brain aging and cognitive health. Dysregulation is a hallmark of aging.
            </p>
          </div>

          <div className="bg-white border border-gray-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Key Longevity Pathways Network</h3>
            <svg width="100%" height="250" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 250">
              <text x="150" y="20" fontFamily="Arial" fontSize="14">Key Longevity Pathways</text>
              <circle cx="200" cy="130" r="40" fill="lightblue" stroke="black"/>
              <text x="175" y="135" fontFamily="Arial" fontSize="12">Brain Health</text>
              {/* Pathway Nodes */}
              <text x="50" y="60" fontFamily="Arial" fontSize="10">IIS (Metabolism)</text>
              <line x1="100" y1="70" x2="170" y2="100" stroke="gray"/>
              <text x="280" y="60" fontFamily="Arial" fontSize="10">mTOR (Autophagy)</text>
              <line x1="270" y1="70" x2="230" y2="100" stroke="gray"/>
              <text x="30" y="135" fontFamily="Arial" fontSize="10">AMPK (Energy)</text>
              <line x1="80" y1="135" x2="160" y2="130" stroke="gray"/>
              <text x="270" y="135" fontFamily="Arial" fontSize="10">NAD+/Sirtuins</text>
              <line x1="260" y1="135" x2="240" y2="130" stroke="gray"/>
              <text x="50" y="210" fontFamily="Arial" fontSize="10">NF-kB (Inflammation)</text>
              <line x1="100" y1="200" x2="170" y2="160" stroke="gray"/>
              <text x="280" y="210" fontFamily="Arial" fontSize="10">Nrf2 (Antioxidant)</text>
              <line x1="270" y1="200" x2="230" y2="160" stroke="gray"/>
            </svg>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="bg-red-50 border border-red-200 p-3 rounded">
                <h4 className="font-medium text-red-800 mb-1">Insulin/IGF-1 (IIS)</h4>
                <p className="text-xs text-gray-700">Neuronal growth, plasticity, glucose metabolism³⁷. Dysregulation → brain insulin resistance</p>
              </div>
              <div className="bg-orange-50 border border-orange-200 p-3 rounded">
                <h4 className="font-medium text-orange-800 mb-1">mTOR</h4>
                <p className="text-xs text-gray-700">Nutrient sensing, growth, autophagy. Hyperactivation → ↓autophagy, ↑senescence</p>
              </div>
              <div className="bg-green-50 border border-green-200 p-3 rounded">
                <h4 className="font-medium text-green-800 mb-1">AMPK</h4>
                <p className="text-xs text-gray-700">Cellular energy sensor, promotes autophagy, mitochondrial biogenesis⁶⁴. ↓Activity in aging</p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="bg-blue-50 border border-blue-200 p-3 rounded">
                <h4 className="font-medium text-blue-800 mb-1">Sirtuins/NAD⁺ Axis</h4>
                <p className="text-xs text-gray-700">DNA repair, stress resistance, neuroprotection⁶⁴. NAD⁺ decline with age impairs sirtuins</p>
              </div>
              <div className="bg-purple-50 border border-purple-200 p-3 rounded">
                <h4 className="font-medium text-purple-800 mb-1">NF-κB</h4>
                <p className="text-xs text-gray-700">Inflammation, immune response. Chronic activation → "inflammaging"⁸³</p>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 p-3 rounded">
                <h4 className="font-medium text-yellow-800 mb-1">Nrf2</h4>
                <p className="text-xs text-gray-700">Master antioxidant response⁸⁸. Declines with age → ↑oxidative stress</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-800 mb-3">qEEG & Neurotech Link</h3>
            <p className="text-sm text-gray-700">
              qEEG reflects functional consequences of pathway dysregulation. Neurotech may modulate these pathways directly/indirectly.
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Adjunctive Nutraceuticals & Peptides",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Concept</h3>
            <p className="text-sm text-gray-700">
              Support underlying biochemical pathways, synergize with neurotechnologies. Personalize based on biomarkers & qEEG.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Foundation</h3>
            <p className="text-sm text-gray-700">
              Optimal nutrition, exercise, sleep, stress management are paramount⁹⁹
            </p>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-800">Selected Examples</h3>
              <p className="text-xs text-red-600">(Clinical Supervision Essential)</p>
              
              <div className="space-y-3">
                <div className="bg-white border border-gray-200 p-3 rounded">
                  <h4 className="font-medium text-gray-800 mb-1">NAD+ Precursors (NMN, NR)</h4>
                  <p className="text-sm text-gray-700">Support sirtuins, mitochondria. (e.g., 250mg-1g+/day)⁶⁴,⁸¹</p>
                </div>
                
                <div className="bg-white border border-gray-200 p-3 rounded">
                  <h4 className="font-medium text-gray-800 mb-1">Omega-3 Fatty Acids (EPA/DHA)</h4>
                  <p className="text-sm text-gray-700">Membrane health, anti-inflammatory. (e.g., 1-2g combined EPA+DHA/day)⁹³</p>
                </div>
                
                <div className="bg-white border border-gray-200 p-3 rounded">
                  <h4 className="font-medium text-gray-800 mb-1">Lion's Mane Mushroom</h4>
                  <p className="text-sm text-gray-700">NGF support, cognitive function. (e.g., 500mg-1g/day)¹⁴²</p>
                </div>
                
                <div className="bg-white border border-gray-200 p-3 rounded">
                  <h4 className="font-medium text-gray-800 mb-1">Low-dose Lithium</h4>
                  <p className="text-sm text-gray-700">Neuroprotection, BDNF. (e.g., 1-5mg elemental/day)⁶³</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-800">Peptides & Advanced</h3>
              
              <div className="space-y-3">
                <div className="bg-white border border-gray-200 p-3 rounded">
                  <h4 className="font-medium text-gray-800 mb-1">Selank</h4>
                  <p className="text-sm text-gray-700">Anxiolytic, cognitive enhancement. (e.g., 250-300mcg intranasal)</p>
                </div>
                
                <div className="bg-white border border-gray-200 p-3 rounded">
                  <h4 className="font-medium text-gray-800 mb-1">Methylene Blue</h4>
                  <p className="text-sm text-gray-700">Mitochondrial support, memory. (e.g., 0.5-1mg/kg)⁷⁶</p>
                </div>
                
                <div className="bg-white border border-gray-200 p-3 rounded">
                  <h4 className="font-medium text-gray-800 mb-1">Plasmalogens</h4>
                  <p className="text-sm text-gray-700">Membrane integrity, myelin support. (e.g., 900mg/day)</p>
                </div>
                
                <div className="bg-white border border-gray-200 p-3 rounded">
                  <h4 className="font-medium text-gray-800 mb-1">C15:0 (Pentadecanoic acid)</h4>
                  <p className="text-sm text-gray-700">AMPK activation, mTOR modulation. (e.g., 100mg/day)⁶¹</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-800 mb-3">Synergy with Neurotechnologies</h3>
            <ul className="text-sm text-gray-700 space-y-2">
              <li>• Pre-NFB supplementation may enhance neuroplasticity</li>
              <li>• Anti-inflammatory support during stimulation protocols</li>
              <li>• Mitochondrial support for energy-demanding brain training</li>
              <li>• Personalization based on qEEG patterns and biomarkers</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      title: "Sample Integrative Protocol: Enhancing Memory",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Example Patient Profile</h3>
            <p className="text-sm text-gray-700">
              65-year-old seeking cognitive optimization; mild subjective memory concerns, no MCI
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white border border-gray-200 p-4 rounded-lg">
              <h4 className="font-medium text-gray-800 mb-3">1. qEEG Assessment</h4>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• Baseline brain mapping</li>
                <li>• Identify: ↑ theta (frontal), ↓ alpha/theta ratio</li>
                <li>• Target areas for intervention</li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 p-4 rounded-lg">
              <h4 className="font-medium text-gray-800 mb-3">2. Neurofeedback Protocol</h4>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• SMR up-training (12-15 Hz) at C3/C4</li>
                <li>• Theta-down training (4-8 Hz)</li>
                <li>• 20 sessions, 2x/week</li>
                <li>• 30 min per session</li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 p-4 rounded-lg">
              <h4 className="font-medium text-gray-800 mb-3">3. Adjunctive Support</h4>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• Lion's Mane 1g daily</li>
                <li>• Omega-3 2g EPA+DHA</li>
                <li>• Low-dose Lithium 1mg</li>
                <li>• Sleep optimization</li>
              </ul>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-4">Timeline & Monitoring</h3>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div className="text-center">
                <div className="bg-white p-3 rounded border mb-2">
                  <strong>Week 0</strong>
                </div>
                <p className="text-gray-700">Baseline qEEG, Cognitive testing, Biomarkers</p>
              </div>
              <div className="text-center">
                <div className="bg-white p-3 rounded border mb-2">
                  <strong>Weeks 1-10</strong>
                </div>
                <p className="text-gray-700">NFB sessions + supplements, Weekly progress</p>
              </div>
              <div className="text-center">
                <div className="bg-white p-3 rounded border mb-2">
                  <strong>Week 10</strong>
                </div>
                <p className="text-gray-700">Mid-point qEEG, Adjust protocols</p>
              </div>
              <div className="text-center">
                <div className="bg-white p-3 rounded border mb-2">
                  <strong>Week 20</strong>
                </div>
                <p className="text-gray-700">Final assessment, Maintenance plan</p>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Expected Outcomes</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-medium text-gray-800 mb-2">qEEG Changes</h4>
                <ul className="text-gray-700 space-y-1">
                  <li>• Normalized theta activity</li>
                  <li>• Improved alpha/theta ratio</li>
                  <li>• Enhanced SMR activity</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-gray-800 mb-2">Functional Improvements</h4>
                <ul className="text-gray-700 space-y-1">
                  <li>• Enhanced working memory</li>
                  <li>• Improved attention span</li>
                  <li>• Better sleep quality</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Ethical Considerations & Future Directions",
      content: (
        <div className="space-y-6">
          <div className="bg-red-50 border border-red-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-red-800 mb-4">Ethical Considerations</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium text-gray-800 mb-2">Enhancement vs. Treatment</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Clear distinction between therapeutic and enhancement applications</li>
                  <li>• Informed consent for elective cognitive enhancement</li>
                  <li>• Managing patient expectations</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-gray-800 mb-2">Safety & Regulation</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Long-term safety data still emerging</li>
                  <li>• Regulatory oversight varies by modality</li>
                  <li>• Need for standardized protocols</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-4">Future Directions</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded border">
                <h4 className="font-medium text-blue-700 mb-2">Technology Advances</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Closed-loop neurofeedback systems</li>
                  <li>• AI-guided protocol optimization</li>
                  <li>• Wearable brain stimulation devices</li>
                </ul>
              </div>
              <div className="bg-white p-4 rounded border">
                <h4 className="font-medium text-blue-700 mb-2">Personalization</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Genetic-guided interventions</li>
                  <li>• Multi-omics integration</li>
                  <li>• Real-time biomarker feedback</li>
                </ul>
              </div>
              <div className="bg-white p-4 rounded border">
                <h4 className="font-medium text-blue-700 mb-2">Clinical Integration</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Standardized assessment protocols</li>
                  <li>• Insurance coverage pathways</li>
                  <li>• Training & certification programs</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-800 mb-3">Research Priorities</h3>
            <ul className="text-sm text-gray-700 space-y-2">
              <li>• Large-scale randomized controlled trials in healthy aging populations</li>
              <li>• Long-term follow-up studies on safety and efficacy</li>
              <li>• Mechanistic studies linking qEEG changes to functional outcomes</li>
              <li>• Optimization of combination protocols (neurofeedback + electroceuticals + nutraceuticals)</li>
            </ul>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Clinical Implementation Readiness</h3>
            <p className="text-sm text-gray-700">
              Current evidence supports cautious implementation in motivated patients with appropriate monitoring and realistic expectations. 
              Continuous education and protocol refinement essential as field evolves.
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Conclusion",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg text-center">
            <h3 className="text-2xl font-light text-blue-800 mb-4">Integrating Brain Longevity Technologies</h3>
            <p className="text-lg text-gray-700 font-light">
              A New Frontier in Preventive Medicine
            </p>
          </div>

          <div className="grid grid-cols-3 gap-6">
            <div className="bg-green-50 border border-green-200 p-6 rounded-lg text-center">
              <svg className="w-12 h-12 mx-auto mb-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h4 className="font-medium text-green-800 mb-2">Evidence-Based</h4>
              <p className="text-sm text-gray-700">Growing body of research supports clinical application in cognitive optimization</p>
            </div>

            <div className="bg-purple-50 border border-purple-200 p-6 rounded-lg text-center">
              <svg className="w-12 h-12 mx-auto mb-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <h4 className="font-medium text-purple-800 mb-2">Personalized</h4>
              <p className="text-sm text-gray-700">qEEG-guided protocols enable targeted, individualized interventions</p>
            </div>

            <div className="bg-orange-50 border border-orange-200 p-6 rounded-lg text-center">
              <svg className="w-12 h-12 mx-auto mb-3 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
              </svg>
              <h4 className="font-medium text-orange-800 mb-2">Integrative</h4>
              <p className="text-sm text-gray-700">Combines neurotechnologies with lifestyle and nutritional interventions</p>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Key Takeaways</h3>
            <ul className="text-sm text-gray-700 space-y-3">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>qEEG provides objective biomarkers</strong> for brain aging and guides personalized intervention strategies</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Neurofeedback enhances neuroplasticity</strong> and has demonstrated efficacy in cognitive domains relevant to aging</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Electroceuticals offer targeted modulation</strong> of brain networks with increasing precision and safety</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Integrative protocols combining neurotechnologies</strong> with nutraceuticals show enhanced efficacy</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Ethical implementation requires</strong> clear informed consent, realistic expectations, and ongoing safety monitoring</span>
              </li>
            </ul>
          </div>

          <div className="bg-blue-900 text-white p-6 rounded-lg text-center">
            <h3 className="text-xl font-light mb-3">The Future of Brain Longevity</h3>
            <p className="text-blue-100 font-light">
              Proactive optimization of cognitive health through advanced neurotechnologies represents a paradigm shift 
              from reactive treatment to preventive enhancement, offering unprecedented opportunities to extend healthspan 
              and cognitive vitality throughout the lifespan.
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Brain Health & Neurotechnologies Through the SHIFT Framework",
      content: (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg text-center">
            <h3 className="text-lg font-medium text-blue-800 mb-2">Neurofeedback, qEEG & Electroceuticals Mapped to Aging Biology</h3>
            <p className="text-sm text-gray-700">How advanced neurotechnologies target core aging pathways in the brain</p>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-white font-bold mr-3">S</div>
                <h3 className="text-md font-medium text-amber-800">Sensing & Neural Signal Processing</h3>
              </div>
              <p className="text-xs text-gray-700 mb-1"><strong>Key Technologies:</strong> qEEG brain mapping, real-time feedback systems, EEG-age biomarkers, alpha peak frequency monitoring</p>
              <p className="text-xs text-gray-700 mb-1"><strong>Mechanisms:</strong> Enhanced neural signal processing, optimized brainwave patterns, improved cognitive efficiency, network connectivity assessment</p>
              <p className="text-xs text-gray-700"><strong>Clinical Impact:</strong> Personalized brain training protocols, cognitive enhancement, attention optimization, executive function improvement</p>
            </div>

            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold mr-3">H</div>
                <h3 className="text-md font-medium text-green-800">Homeostatic Neural Defense & Resilience</h3>
              </div>
              <p className="text-xs text-gray-700 mb-1"><strong>Key Technologies:</strong> Neurofeedback training, stress response protocols, HRV-guided interventions, adaptive brain training</p>
              <p className="text-xs text-gray-700 mb-1"><strong>Mechanisms:</strong> Enhanced stress resilience, improved autonomic regulation, strengthened neural networks, cognitive reserve building</p>
              <p className="text-xs text-gray-700"><strong>Clinical Impact:</strong> Increased stress tolerance, better emotional regulation, enhanced cognitive flexibility, neuroprotective effects</p>
            </div>

            <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white font-bold mr-3">I</div>
                <h3 className="text-md font-medium text-red-800">Neuroinflammation & Immune Modulation</h3>
              </div>
              <p className="text-xs text-gray-700 mb-1"><strong>Key Technologies:</strong> tDCS/tACS stimulation, VNS therapy, neurofeedback protocols targeting inflammation markers</p>
              <p className="text-xs text-gray-700 mb-1"><strong>Mechanisms:</strong> Reduced microglial activation, modulated cytokine production, enhanced vagal tone, anti-inflammatory pathways</p>
              <p className="text-xs text-gray-700"><strong>Clinical Impact:</strong> Decreased neuroinflammation, improved mood regulation, reduced cognitive decline, enhanced neural recovery</p>
            </div>

            <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold mr-3">F</div>
                <h3 className="text-md font-medium text-purple-800">Neural Structural Remodeling & Plasticity</h3>
              </div>
              <p className="text-xs text-gray-700 mb-1"><strong>Key Technologies:</strong> TMS protocols, focused neurofeedback, neuroplasticity-enhancing stimulation, network-based interventions</p>
              <p className="text-xs text-gray-700 mb-1"><strong>Mechanisms:</strong> Synaptic strengthening, dendritic growth, myelin integrity, neural pathway optimization, white matter enhancement</p>
              <p className="text-xs text-gray-700"><strong>Clinical Impact:</strong> Enhanced neuroplasticity, improved cognitive performance, structural brain improvements, accelerated learning</p>
            </div>

            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold mr-3">T</div>
                <h3 className="text-md font-medium text-blue-800">Neural Timekeeping & Circadian Optimization</h3>
              </div>
              <p className="text-xs text-gray-700 mb-1"><strong>Key Technologies:</strong> Circadian neurofeedback, light therapy protocols, sleep-optimized brain training, chronobiological EEG monitoring</p>
              <p className="text-xs text-gray-700 mb-1"><strong>Mechanisms:</strong> Circadian rhythm entrainment, sleep architecture optimization, glymphatic system enhancement, neural timing coordination</p>
              <p className="text-xs text-gray-700"><strong>Clinical Impact:</strong> Improved sleep quality, enhanced cognitive timing, optimized neural repair cycles, better memory consolidation</p>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="text-md font-medium text-gray-800 mb-2">Clinical Integration Strategy for Brain Longevity</h3>
            <p className="text-xs text-gray-700">Neurotechnologies work synergistically across SHIFT domains to enhance brain resilience and cognitive longevity. qEEG provides the diagnostic foundation, neurofeedback enables self-regulation training, and electroceuticals offer targeted interventions. Combined protocols targeting multiple SHIFT domains simultaneously optimize neuroplasticity and cognitive reserve for enhanced brain healthspan.</p>
          </div>
        </div>
      )
    },
    {
      title: "Questions & Discussion",
      content: (
        <div className="flex flex-col items-center justify-center h-full text-center">
          <svg className="w-24 h-24 text-blue-600 mb-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h1 className="text-4xl font-light text-gray-900 mb-6">
            Questions & Discussion
          </h1>
          <p className="text-lg text-gray-600 mb-8">
            Let's explore how these neurotechnologies can be integrated into your clinical practice
          </p>
          <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg max-w-2xl">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Discussion Topics</h3>
            <ul className="text-sm text-gray-700 space-y-2 text-left">
              <li>• Implementation strategies for your patient population</li>
              <li>• Equipment and training considerations</li>
              <li>• Integration with existing treatment protocols</li>
              <li>• Patient selection criteria and contraindications</li>
              <li>• Regulatory and reimbursement considerations</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      title: "Selected References",
      content: (
        <div className="grid grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-medium text-gray-800 mb-4">Primary References</h3>
            <div className="space-y-3 text-xs text-gray-700">
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>1.</strong> PMC9888009 (Neurocognitive Aging)
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>2.</strong> PMC11051307 (EEG, Physical Activity, Aging)
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>3.</strong> PMC11330810 (Synaptic Plasticity, Longevity)
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>7.</strong> PMC11965894 (Parietal Alpha/Theta & Cognitive Training)
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>8.</strong> PubMed 39415522 (qEEG Review)
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>10.</strong> PMC12055827 (qEEG Biomarkers AD/MCI)
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>11.</strong> PMC4892319 (Neurofeedback Review)
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>13.</strong> ExplorationPub 100674 (Neurostimulation AD)
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>21.</strong> PMC11911306 (EEG-Age)
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>26.</strong> PMC7481574 (qEEG-NFB ADHD)
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>30.</strong> PMC6306463 (SMR NFB & WM in Elderly)
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="text-lg font-medium text-gray-800 mb-4">Additional References</h3>
            <div className="space-y-3 text-xs text-gray-700">
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>43.</strong> PubMed 33389281 (NFB & Aging Cognitive Decline Review)
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>44.</strong> MDPI 1648-9144/60/3/369 (Jiang et al., NFB Meta-analysis)
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>56.</strong> PMC8186669 (Electroceuticals GI - VNS mechanisms)
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>61.</strong> MDPI 1422-0067/26/8/3746 (C15:0 Review)
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>63.</strong> PMC12065699 (Lithium Neuroprotection)
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>64.</strong> PMC8656451 (Sirtuins & Longevity)
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>67.</strong> PMC6787556 (NAD+ Brain Aging)
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>76.</strong> PMC3265679 (Methylene Blue Memory/Neuroprotection)
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>83.</strong> PMC11263280 (Neuroinflammation Editorial)
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>88.</strong> PMC10046499 (Nrf2 & AD)
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>142.</strong> PMC12018234 (Lion's Mane Cognition/Mood)
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumbs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <nav className="flex items-center space-x-2 text-sm">
            <Link href="/">
              <Home className="w-4 h-4 text-gray-500 hover:text-gray-700" />
            </Link>
            <span className="text-gray-400">/</span>
            <Link href="/mentorship" className="text-gray-600 hover:text-gray-800">
              Mentorship Program
            </Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900 font-medium">Brain Health: Neurofeedback, Quantitative EEG, Electroceuticals</span>
          </nav>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <Link href="/mentorship" className="inline-flex items-center text-blue-600 hover:text-blue-800">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Mentorship Program
            </Link>
            <LectureDownloader lectureName="Brain Health Neurofeedback" fileName="BrainHealthNeurofeedback" />
          </div>
          <h1 className="text-3xl font-light text-gray-900 mb-2">Brain Health: Neurofeedback, Quantitative EEG, Electroceuticals</h1>
          <p className="text-gray-600 font-light">
            Integrating Advanced Neurotechnologies for Enhanced Healthspan and Cognitive Longevity
          </p>
        </div>

        {/* Slideshow Container */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Slide Content */}
          <div className="relative">
            <div className="h-[500px] p-8 bg-gradient-to-br from-gray-50 to-white">
              <div className="h-full flex flex-col">
                <h2 className="text-2xl font-light text-gray-900 mb-6 text-center">
                  {slides[currentSlide].title}
                </h2>
                <div className="flex-1 overflow-y-auto">
                  {slides[currentSlide].content}
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Controls */}
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
            <button
              onClick={prevSlide}
              disabled={currentSlide === 0}
              className="flex items-center space-x-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-light"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Previous</span>
            </button>

            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600 font-light">
                {currentSlide + 1} of {slides.length}
              </span>
            </div>

            {/* Slide Indicators */}
            <div className="flex space-x-1">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-2 h-2 rounded-full ${
                    index === currentSlide ? 'bg-purple-600' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={nextSlide}
              disabled={currentSlide === slides.length - 1}
              className="flex items-center space-x-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-light"
            >
              <span>Next</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Embedded Audio Lecture */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mt-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Accompanying Audio Lecture</h3>
            <p className="text-sm text-gray-600 mt-1">
              Listen to the comprehensive audio guide while navigating through the slides
            </p>
          </div>
          <div className="p-6 flex justify-center">
            <iframe 
              src="https://share.descript.com/embed/65R84JDkJI6" 
              width="640" 
              height="360" 
              frameBorder="0" 
              allowFullScreen
              className="rounded-lg shadow-sm"
              title="Brain Health Neurofeedback Audio Lecture"
            ></iframe>
          </div>
        </div>
      </div>

      {/* Keyboard Navigation */}
      <div className="fixed bottom-4 right-4 bg-white p-2 rounded-lg shadow-md">
        <p className="text-xs text-gray-500 font-light">Use ← → arrows to navigate</p>
      </div>
    </div>
  );
}